import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, apiPost, apiPut, operationsClient } from '@/lib/api/client';
import { zapierEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';

/**
 * A single OAuth connection granted to an external app (e.g. Zapier) through the
 * operations OAuth provider. The consent / token exchange happens inside the
 * external app; this shape is what the operations `GET /oauth/connections`
 * endpoint returns for display + revocation on the Connected Apps page.
 */
export interface ZapierConnection {
  id: string;
  clientId: string;
  userId: string;
  clientName: string;
  /** The consenting staff member's display name (or email), resolved server
   * side. Falls back to the raw `userId` when that account no longer exists.
   *
   * Optional on purpose: an operations build that predates this field omits
   * it entirely. A mixed PR can't skew (`fast-fe-deploy` defers to the CDK
   * pipeline whenever `apps/operations/**` is touched), but a later FE-only
   * merge ships in ~3 min from a main that already carries this code while
   * the backend is still mid-CDK — and a backend rollback does the same. Read
   * it with a `|| userId` fallback, never bare. */
  userLabel?: string;
  scopes: string[];
  createdAt: string;
  /** The acquisition channel this connection's leads are attributed to. */
  acquisitionChannelId: string | null;
  /** When a lead last arrived on it. Null means never — including after a revoke. */
  lastReceivedAt: string | null;
}

/**
 * GET /oauth/connections — list the OAuth connections (Zapier and other apps)
 * authorized for the current tenant. Errors are surfaced quietly so the
 * Connected Apps page can still render the other integration cards.
 */
export function useZapierConnections() {
  return useApiQuery<ZapierConnection[]>({
    queryKey: settingsKeys.zapierConnections(),
    queryFn: () => apiGet<ZapierConnection[]>(operationsClient, zapierEndpoints.connections()),
    showErrorToast: false,
  });
}

/** Backend-served Zapier config: the per-environment "Connect" deep-link. */
export interface ZapierConfig {
  connectUrl: string;
}

/**
 * GET /oauth/zapier/config — the per-environment Zapier "Connect" URL. Sourced
 * from backend runtime config (not a frontend build var) so the same bundle
 * serves every environment and the card's visibility is a runtime concern.
 * `connectUrl` is empty when the integration is not configured for this
 * environment. Errors are surfaced quietly so the Connected Apps page still
 * renders the other integration cards.
 */
export function useZapierConfig() {
  return useApiQuery<ZapierConfig>({
    queryKey: settingsKeys.zapierConfig(),
    queryFn: () => apiGet<ZapierConfig>(operationsClient, zapierEndpoints.config()),
    showErrorToast: false,
  });
}

/**
 * POST /oauth/connections/:id/revoke — revoke (disconnect) an OAuth connection.
 */
export function useRevokeZapierConnection() {
  const queryClient = useQueryClient();
  return useApiMutation<void, string>({
    mutationFn: (id) => apiPost<void>(operationsClient, zapierEndpoints.revokeConnection(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.zapierConnections() });
      toast.success('Zapier connection removed');
    },
  });
}

/** Variables for {@link useSetConnectionChannel}. */
export interface SetConnectionChannelVariables {
  id: string;
  /**
   * Required, never optional. The backend DTO has no `@IsOptional()` on
   * purpose — an omitted or `undefined` field 400s rather than silently
   * skipping a cross-tenant ownership check. `null` is the deliberate
   * "unbind" value, not an absence of one.
   */
  acquisitionChannelId: string | null;
}

/** Result of {@link useSetConnectionChannel}. */
export interface SetConnectionChannelResult {
  acquisitionChannelId: string | null;
}

/**
 * PUT /oauth/connections/:id/channel — bind (or unbind, via `null`) the
 * acquisition channel an OAuth connection's inbound leads are attributed to.
 */
export function useSetConnectionChannel() {
  const queryClient = useQueryClient();
  return useApiMutation<SetConnectionChannelResult, SetConnectionChannelVariables>({
    mutationFn: ({ id, acquisitionChannelId }) =>
      apiPut<SetConnectionChannelResult, { acquisitionChannelId: string | null }>(
        operationsClient,
        zapierEndpoints.connectionChannel(id),
        { acquisitionChannelId },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.zapierConnections() });
      toast.success('Connection channel updated');
    },
  });
}
